import React, { useState, useEffect } from 'react';
import CoachPlayerUpdatesTab from './CoachPlayerUpdatesTab';
import { playerUpdates } from '../../api';
import './CoachUpdatesTab.css';

/**
 * CoachUpdatesOverviewTab - Dashboard showing all students, pick one to view their updates.
 * Takes the coach's roster as a `students` prop (shape: {_id, name, ...}, same as
 * CoachView's `players`) rather than deriving a player list from notes - a student
 * with zero notes still has a coachId link and should still show up here.
 */
const CoachUpdatesOverviewTab = ({ students = [], navTarget = null }) => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [pendingByPlayer, setPendingByPlayer] = useState({});

  const loadPendingCounts = async () => {
    try {
      const response = await playerUpdates.getPendingByPlayer();
      setPendingByPlayer(response.data.pendingByPlayer || {});
    } catch (err) {
      console.error('Error loading pending update counts:', err);
    }
  };

  // Notification tap for a specific student: open their updates directly
  useEffect(() => {
    if (!navTarget || navTarget.tab !== 'updates' || !navTarget.playerId) return;
    const student = students.find(s => s._id === navTarget.playerId);
    if (student) setSelectedPlayer({ id: student._id, name: student.name || 'Unknown Player' });
  }, [navTarget, students]);

  // Load on mount, and again whenever we return to the list (selectedPlayer
  // goes back to null) - the coach may have just marked updates as
  // answered/resolved on the student's own updates screen, which should
  // clear or shrink that student's badge here.
  useEffect(() => {
    if (!selectedPlayer) {
      loadPendingCounts();
    }
  }, [selectedPlayer]);

  // If a player is selected, show their updates
  if (selectedPlayer) {
    return (
      <CoachPlayerUpdatesTab
        playerId={selectedPlayer.id}
        playerName={selectedPlayer.name}
        onBack={() => setSelectedPlayer(null)}
      />
    );
  }

  // Students with a pending update (e.g. a newly shared match) always
  // float to the top, most-pending-first; everything else stays
  // alphabetical within each group.
  const filteredStudents = students
    .map(s => ({ id: s._id, name: s.name || 'Unknown Player' }))
    .filter(student =>
      student.name.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      const pendingA = pendingByPlayer[a.id] || 0;
      const pendingB = pendingByPlayer[b.id] || 0;
      if (pendingA !== pendingB) return pendingB - pendingA;
      return a.name.localeCompare(b.name);
    });

  return (
    <div className="coach-updates-overview-tab">
      <div className="overview-header">
        <h3>📮 Player Updates</h3>
        <p className="overview-subtext">
          View match shares and thoughts from your students
        </p>
      </div>

      {/* Search Bar */}
      <div className="search-section">
        <input
          type="text"
          className="search-input"
          placeholder="Search players..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Empty State */}
      {students.length === 0 && (
        <div className="empty-state">
          <p>No students yet</p>
          <p>Add students to start receiving their updates</p>
        </div>
      )}

      {/* Students List */}
      {students.length > 0 && (
        <>
          {filteredStudents.length === 0 ? (
            <div className="empty-state">
              <p>No students found matching "{searchQuery}"</p>
            </div>
          ) : (
            <div className="students-grid">
              {filteredStudents.map(student => {
                const pendingCount = pendingByPlayer[student.id] || 0;
                return (
                  <div
                    key={student.id}
                    className={`student-card ${pendingCount > 0 ? 'has-notification' : ''}`}
                    onClick={() => setSelectedPlayer(student)}
                  >
                    {pendingCount > 0 && (
                      <span className="notification-badge">{pendingCount}</span>
                    )}
                    <div className="student-avatar">
                      {student.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="student-info">
                      <h4>{student.name}</h4>
                      <p className="student-hint">
                        {pendingCount > 0
                          ? `${pendingCount} new update${pendingCount !== 1 ? 's' : ''}`
                          : 'View updates'}
                      </p>
                    </div>
                    <div className="student-action">
                      <span className="arrow">→</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default CoachUpdatesOverviewTab;
